import React, { PropsWithChildren, useEffect, useMemo, useState } from 'react';
import { Cart, CartContext, CartItem, CartState, DEFAULT_CART } from './CartContext';
import { cartStorage } from './CartStorageProvider';
import { APP_CONFIG } from '../shared/config';
import {
  createUnitedWaySupportItem,
  getUnitedWayAgencyId,
  getUnitedWaySupportAmount,
  hasUnitedWaySupport,
  matchesUnitedWayAgency,
  withoutUnitedWaySupport,
} from './unitedWaySupport';

export const CartProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [cartState, setCartState] = useState<CartState>(() => cartStorage.read());

  // Lives above the checkout routes so navigating mid-charge can't reset the lock.
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    cartStorage.write(cartState);
  }, [cartState]);

  const cart = useMemo<Cart>(() => {
    const unitedWayAgencyId = getUnitedWayAgencyId();

    return {
      ...cartState,

      supportUnitedWay: hasUnitedWaySupport(cartState.items),
      isSubmitting,

      addItem: (item: CartItem) =>
        setCartState(prev => {
          // A regular United Way donation replaces the $1 add-on, whose checkbox is then hidden.
          const current = matchesUnitedWayAgency(item, unitedWayAgencyId)
            ? withoutUnitedWaySupport(prev.items)
            : prev.items;

          const items = [...current, item];
          return { ...prev, items, ...calculateCartAmounts(items, prev.coverProcessingFee) };
        }),

      updateItem: (index: number, values: CartItem) =>
        setCartState(prev => {
          const items = prev.items.map((item, i) => (i === index ? { ...item, ...values } : item));

          return { ...prev, items, ...calculateCartAmounts(items, prev.coverProcessingFee) };
        }),

      removeItem: (index: number) =>
        setCartState(prev => {
          const items = prev.items.filter((_, i) => i !== index);

          // reset raffleAgencyId to null if the current raffle pick
          // no longer has any donations in the cart.
          const raffleAgencyId = items.some(item => item.agencyId === prev.raffleAgencyId)
            ? prev.raffleAgencyId
            : null;

          return {
            ...prev,
            items,
            raffleAgencyId,
            ...calculateCartAmounts(items, prev.coverProcessingFee),
          };
        }),

      clear: () => setCartState(DEFAULT_CART),

      setBillingInfo: billingInfo => setCartState(prev => ({ ...prev, billingInfo })),

      setPaymentMethod: paymentMethod => setCartState(prev => ({ ...prev, paymentMethod })),

      setCoverProcessingFee: (coverProcessingFee: boolean) =>
        setCartState(prev => ({
          ...prev,
          coverProcessingFee,
          ...calculateCartAmounts(prev.items, coverProcessingFee),
        })),

      setRaffleAgencyId: raffleAgencyId => setCartState(prev => ({ ...prev, raffleAgencyId })),

      // Rebuilt rather than appended so repeated toggles can't duplicate the item.
      setSupportUnitedWay: (enabled: boolean) =>
        setCartState(prev => {
          const items = withoutUnitedWaySupport(prev.items);

          // Removal stays available without a configured id so stale add-ons can be cleaned up.
          if (!enabled) {
            if (items.length === prev.items.length) return prev;
            return { ...prev, items, ...calculateCartAmounts(items, prev.coverProcessingFee) };
          }

          if (!unitedWayAgencyId || items.length >= APP_CONFIG.Donately.CartItemLimit) return prev;

          items.push(createUnitedWaySupportItem(unitedWayAgencyId, getUnitedWaySupportAmount()));

          return { ...prev, items, ...calculateCartAmounts(items, prev.coverProcessingFee) };
        }),

      setSubmitting: setIsSubmitting,
    };
  }, [cartState, isSubmitting]);

  return <CartContext.Provider value={cart}>{children}</CartContext.Provider>;
};

function calculateCartAmounts(items: ReadonlyArray<CartItem>, coverProcessingFee: boolean) {
  const baseAmount = items.reduce((acc, x) => acc + x.amount, 0);

  // Stripe calculates donor covered fees as
  // (baseAmount / (1 - APP_CONFIG.Donately.ProcessingFeePercent / 100)) - baseAmount
  // This simplified calculation was requested by the client.
  const fees = Math.trunc(baseAmount * (APP_CONFIG.Donately.ProcessingFeePercent / 100));

  const total = coverProcessingFee ? baseAmount + fees : baseAmount;

  return { baseAmount, fees, total };
}
